/**
 * Akış level generator. Level N = generateLevel(N, pool): unbounded, seeded, graded by the bots and
 * regenerated while "broken" (unsolvable by the lookahead bot or <0.3 win rate for the noisy bot).
 *
 * Construction (so that most boards are solvable by design, the bot only confirms it):
 *  1. COVER: Voronoi-style blobby color regions (abstract, unrelated to the hidden picture).
 *  2. AMMO: each color's block count is cut into shooters of 5..20 ammo (ammo per color == blocks).
 *  3. REFERENCE ORDER: a greedy peel of the cover tells in which order the shooters are useful.
 *  4. QUEUES: that order is dealt into 3..5 columns, then shuffled by `shuffle` (interleaving) —
 *     0 = the fronts always hold the next useful shooter (tutorial levels), 1 = heavy blocking.
 * Cover and queues depend only on (n, attempt); the picture is chosen separately from the pool, so
 * tests with synthetic pictures get the very same boards.
 *
 * From level 21 on the chapter designer (@enjoyment/core designer.ts) picks each level's difficulty (one of the
 * levels 15–30 of the ramp below) and the chapter's twist; the seed and the picture still follow the real level
 * number, and the noisy bot must land in the designed slot's win-rate band (warm-ups and breathers easier, hard
 * levels and the finale harder).
 */
import { decodePixels, designLevel, hashSeed, Rng, type DesignSpec, type DesignedLevel, type Picture } from "@enjoyment/core";
import { grade, type Grade } from "./bot";
import { runLap } from "./sim";
import type { Level, Shooter } from "./types";

export interface Params {
  size: number;
  colors: number;
  cols: number;
  trayCap: number;
  /** Number of Voronoi seeds in the cover. */
  regions: number;
  /** 0..1: fraction of the reference order that gets scrambled. */
  shuffle: number;
  /** 0..1: 0 = few big shooters, 1 = many small ones. */
  split: number;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Chapter design (levels 21+). Floor 15: past the tutorial (1–3) and the last layout change (5 queues, 4-slot tray);
 * the designer's easiest slot maps to ramp level ~21, so every designed level has all 6 colors. Ceiling 30: the
 * ramp saturates there. Twists are knobs the ramp already has: a chapter of full-size 20×20 boards (`wide`), of
 * busier covers with half again as many color regions (`patchwork`), of few big shooters with a roomy 5-slot tray
 * (`heavy`), or of 4 queues instead of 5 (`narrow`).
 */
export const DESIGN: DesignSpec = { game: "akis", floor: 15, ceiling: 30, twists: ["wide", "patchwork", "heavy", "narrow"] };

/** Parameters of level n: the hand-tuned ramp for 1–20, the chapter designer from 21 on. */
export function paramsFor(n: number): Params {
  const d = designLevel(DESIGN, n);
  return d ? designedParams(d) : rampParams(n);
}

/** The ramp's parameters at the designed level's `effective`, with the chapter's twist on top. */
function designedParams(d: DesignedLevel): Params {
  const p = rampParams(d.effective);
  switch (d.twist) {
    case "wide":
      return { ...p, size: 20 };
    case "patchwork":
      return { ...p, regions: Math.round(p.regions * 1.5) };
    case "heavy":
      return { ...p, split: 0, trayCap: 5 };
    case "narrow":
      return { ...p, cols: 4 };
    default:
      return p;
  }
}

/** Difficulty ramp (levels 1–20, and the scale the designer draws from). Levels 1–3 are tutorial-easy; saturates at 30. */
export function rampParams(n: number): Params {
  const t = clamp01((n - 1) / 29);
  const size = Math.min(20, 12 + Math.floor(8 * t + 1e-9));
  const colors = n <= 3 ? 3 : n <= 9 ? 4 : n <= 18 ? 5 : 6;
  const cols = n <= 5 ? 3 : n <= 14 ? 4 : 5;
  const trayCap = n <= 14 ? 5 : 4;
  const early = n <= 3;
  const regions = colors + (early ? 1 : Math.round(colors * (0.6 + 3 * t)));
  const shuffle = early ? 0 : 0.1 + 0.45 * t;
  const split = early ? 0.55 : clamp01(0.8 - 0.7 * t);
  return { size, colors, cols, trayCap, regions, shuffle, split };
}

export const MAX_ATTEMPTS = 14;

function shuffleInPlace<T>(rng: Rng, a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

function makeCover(rng: Rng, p: Params): number[] {
  const { size, colors, regions } = p;
  const minBlocks = Math.max(12, Math.floor((size * size) / (colors * 3.2)));
  let best: number[] = [];
  for (let tries = 0; tries < 30; tries++) {
    const seeds: { x: number; y: number; color: number; w: number }[] = [];
    const palette = shuffleInPlace(rng, Array.from({ length: colors }, (_, i) => i));
    for (let i = 0; i < regions; i++) {
      seeds.push({ x: rng.next() * size, y: rng.next() * size, color: i < colors ? palette[i]! : rng.int(colors), w: 0.65 + rng.next() * 0.9 });
    }
    const cells = new Array<number>(size * size).fill(0);
    for (let r = 0; r < size; r++)
      for (let c = 0; c < size; c++) {
        let bestD = Infinity;
        let col = 0;
        for (const s of seeds) {
          const dx = c + 0.5 - s.x;
          const dy = r + 0.5 - s.y;
          const d = (dx * dx + dy * dy) * s.w + rng.next() * size * 0.35;
          if (d < bestD) {
            bestD = d;
            col = s.color;
          }
        }
        cells[r * size + c] = col;
      }
    // One majority pass removes single-cell speckles so regions read as blobs.
    const sm = cells.slice();
    for (let r = 0; r < size; r++)
      for (let c = 0; c < size; c++) {
        const cnt = new Array<number>(colors).fill(0);
        for (let dr = -1; dr <= 1; dr++)
          for (let dc = -1; dc <= 1; dc++) {
            const rr = r + dr;
            const cc = c + dc;
            if (rr >= 0 && rr < size && cc >= 0 && cc < size) cnt[cells[rr * size + cc]!]!++;
          }
        let m = 0;
        for (let k = 1; k < colors; k++) if (cnt[k]! > cnt[m]!) m = k;
        if (cnt[m]! >= 5) sm[r * size + c] = m;
      }
    const counts = new Array<number>(colors).fill(0);
    for (const v of sm) counts[v]!++;
    best = sm;
    if (counts.every((x) => x >= minBlocks)) return sm;
  }
  return best;
}

/** Cut `n` ammo into k shooters within 5..20 (a color with <5 blocks yields one small shooter). */
function chunk(rng: Rng, n: number, split: number): number[] {
  if (n <= 5) return [n];
  const kmin = Math.ceil(n / 20);
  const kmax = Math.max(kmin, Math.floor(n / 5));
  const k = kmin + Math.round(split * (kmax - kmin));
  const parts = new Array<number>(k).fill(5);
  let left = n - 5 * k;
  while (left > 0) {
    const i = rng.int(k);
    if (parts[i]! < 20) {
      parts[i]!++;
      left--;
    }
  }
  for (let i = 0; i < k * 2; i++) {
    const a = rng.int(k);
    const b = rng.int(k);
    const mv = 1 + rng.int(3);
    if (a !== b && parts[a]! - mv >= 5 && parts[b]! + mv <= 20) {
      parts[a]! -= mv;
      parts[b]! += mv;
    }
  }
  return parts;
}

/**
 * Greedy peel of a copy of the cover: which fresh shooter should be launched next? Tray leftovers
 * are re-launched in the simulation exactly like a player would. Returns the fresh-launch order.
 */
function referenceOrder(rng: Rng, cover: number[], size: number, trayCap: number, byColor: Shooter[][]): Shooter[] {
  const cov = Int8Array.from(cover);
  const scratch = new Int8Array(cov.length);
  const pending = byColor.map((l) => l.slice());
  const tray: Shooter[] = [];
  const order: Shooter[] = [];
  let remaining = cov.length;
  for (const v of cov) if (v < 0) remaining--;
  for (let guard = 0; remaining > 0 && guard < 2000; guard++) {
    let bestV = -Infinity;
    let pick: { from: "p" | "t"; i: number } | null = null;
    const consider = (sh: Shooter, from: "p" | "t", i: number) => {
      scratch.set(cov);
      const lap = runLap(scratch, size, sh.color, sh.ammo);
      if (lap.shots === 0) return;
      const parks = lap.ammoLeft > 0 && lap.shots < remaining;
      let v = lap.shots * 10 + (lap.ammoLeft === 0 ? 6 : 0) + rng.next();
      if (parks) v -= tray.length * 5 + (from === "p" ? 0 : -3);
      if (parks && from === "p" && tray.length >= trayCap) v -= 500;
      if (v > bestV) {
        bestV = v;
        pick = { from, i };
      }
    };
    pending.forEach((l, c) => {
      if (l.length) consider(l[0]!, "p", c);
    });
    tray.forEach((sh, i) => consider(sh, "t", i));
    if (!pick) break;
    const chosen: { from: "p" | "t"; i: number } = pick;
    let sh: Shooter;
    if (chosen.from === "p") {
      sh = pending[chosen.i]!.shift()!;
      order.push(sh);
    } else sh = tray.splice(chosen.i, 1)[0]!;
    const lap = runLap(cov, size, sh.color, sh.ammo);
    remaining -= lap.shots;
    if (lap.ammoLeft > 0 && remaining > 0) tray.push({ ...sh, ammo: lap.ammoLeft });
  }
  for (const l of pending) for (const sh of l) order.push(sh); // leftovers (should be none)
  return order;
}

export interface BuildOpts {
  /** Force an unshuffled queue (the safe fallback). */
  tame?: boolean;
  /** Override ramp values (experiments / tests). */
  params?: Partial<Params>;
}

/** One candidate board for (n, attempt). Picture is attached by the caller. */
export function buildBoard(n: number, attempt = 0, opts: BuildOpts = {}): Level {
  const p = { ...paramsFor(n), ...opts.params };
  const seed = hashSeed(attempt === 0 ? `akis:${n}` : `akis:${n}:${attempt}`);
  const rng = new Rng(seed);
  const cover = makeCover(rng, p);
  const counts = new Array<number>(p.colors).fill(0);
  for (const v of cover) counts[v]!++;
  let nextId = 0;
  const byColor: Shooter[][] = counts.map((cnt, color) => shuffleInPlace(rng, chunk(rng, cnt, p.split)).map((ammo) => ({ id: nextId++, color, ammo })));
  const order = referenceOrder(rng, cover, p.size, p.trayCap, byColor);
  const shuffle = opts.tame ? 0 : p.shuffle;
  const swaps = Math.round(order.length * shuffle * 1.5);
  const window = Math.max(2, Math.round(p.cols * (1 + 3 * shuffle)));
  for (let i = 0; i < swaps; i++) {
    const a = rng.int(order.length);
    const b = Math.min(order.length - 1, a + 1 + rng.int(window));
    [order[a], order[b]] = [order[b]!, order[a]!];
  }
  const queues: Shooter[][] = Array.from({ length: p.cols }, () => []);
  for (let r = 0; r * p.cols < order.length; r++) {
    const cols = shuffleInPlace(rng, Array.from({ length: p.cols }, (_, i) => i));
    for (let k = 0; k < p.cols; k++) {
      const sh = order[r * p.cols + k];
      if (sh) queues[cols[k]!]!.push(sh);
    }
  }
  return { id: `akis-${n}`, n, seed, size: p.size, colors: p.colors, trayCap: p.trayCap, cover, queues, pictureId: "", attempt };
}

/** Pictures with enough opaque pixels to be recognisable through a 12–20 grid. */
export function usablePictures(pool: Picture[]): Picture[] {
  const good = pool.filter((p) => {
    let opaque = 0;
    for (const v of decodePixels(p)) if (v >= 0) opaque++;
    return opaque >= p.size * p.size * 0.35;
  });
  return good.length ? good : pool;
}

export function pictureFor(n: number, pool: Picture[]): Picture | null {
  const list = usablePictures(pool);
  if (!list.length) return null;
  return list[hashSeed(`akis:pic:${n}`) % list.length]!;
}

const boardCache = new Map<string, Level>();

export interface GenOpts {
  /** Noisy-bot samples per grade (default 12). */
  samples?: number;
}

/** Level N for a picture pool. Deterministic: same N + same pool => same level. */
export function generateLevel(n: number, pool: Picture[] = [], opts: GenOpts = {}): Level {
  const samples = opts.samples ?? 12;
  const key = `${n}:${samples}`;
  let board = boardCache.get(key);
  if (!board) {
    // Designed levels (21+) want the noisy bot inside their slot's win-rate band; failing that, the playable
    // attempt closest to the band's target.
    const band = designLevel(DESIGN, n)?.winRate ?? null;
    let bestLevel: Level | null = null;
    let bestScore = -1;
    let nearest: Level | null = null;
    let nearestDist = Infinity;
    for (let attempt = 0; attempt < MAX_ATTEMPTS && !board; attempt++) {
      const lvl = buildBoard(n, attempt, { tame: attempt >= MAX_ATTEMPTS - 3 });
      const g = grade(lvl, samples);
      lvl.difficulty = g.difficulty === "broken" ? undefined : g.difficulty;
      lvl.winRate = g.winRate;
      const okBand = band ? g.winRate >= band.min && g.winRate <= band.max : n > 3 || g.difficulty === "easy";
      if (g.difficulty !== "broken" && okBand) board = lvl;
      else if (band && g.difficulty !== "broken") {
        const dist = Math.abs(g.winRate - band.target);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = lvl;
        }
      } else if (g.greedyWins && g.winRate > bestScore) {
        bestScore = g.winRate;
        bestLevel = lvl;
      }
    }
    board = board ?? nearest ?? bestLevel ?? buildBoard(n, MAX_ATTEMPTS - 1, { tame: true });
    boardCache.set(key, board);
  }
  const pic = pictureFor(n, pool);
  return { ...board, pictureId: pic?.id ?? "" };
}

export { grade, type Grade };
